/**
 * Revenue-related debug field definitions
 */

import type { DebugFieldDefinition } from '../types';
import { formatBoolean, formatCurrency, formatMillage, formatPercent } from './formatters';

export const revenueFields: DebugFieldDefinition[] = [
  {
    id: 'revenue.paymentPortion',
    name: 'Payment Revenue Portion',
    category: 'revenue',
    description: 'Revenue portion allocated to this payment',
    extractor: (ctx) => ctx.cashFlow?.paymentRevenuePortion,
    formatter: formatCurrency,
    formula: (ctx) => {
      const cf = ctx.cashFlow;
      if (!cf) return undefined;
      return `Total Revenue ($${cf.totalRevenueForTaxYear?.toLocaleString() ?? 'N/A'}) × ${(cf.paymentRevenuePercentage * 100).toFixed(0)}% = $${cf.paymentRevenuePortion?.toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'revenue.minusExistingDebt',
    name: 'Revenue After Existing Debt',
    category: 'revenue',
    description: 'Revenue remaining after existing debt payments',
    extractor: (ctx) => ctx.cashFlow?.revenueMinusExistingDebt,
    formatter: formatCurrency,
    formula: (ctx) => {
      const cf = ctx.cashFlow;
      if (!cf) return undefined;
      return `Revenue ($${cf.paymentRevenuePortion?.toLocaleString()}) - Existing Debt ($${cf.debtPaymentAmount?.toLocaleString()}) = $${cf.revenueMinusExistingDebt?.toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'revenue.availableForBonds',
    name: 'Available for Bonds',
    category: 'revenue',
    description: 'Revenue available after interest obligations',
    extractor: (ctx) => {
      // Prefer decisionContext (accurate value at calculation time)
      if (ctx.decisionContext?.currentAvailableForBonds !== undefined) {
        return ctx.decisionContext.currentAvailableForBonds;
      }
      // Fallback to cashFlow (only accurate after applyDistributionPlan)
      return ctx.cashFlow?.availableForBonds;
    },
    formatter: formatCurrency,
    formula: (ctx) => {
      const cf = ctx.cashFlow;
      if (!cf) return undefined;
      // Use decisionContext values for accuracy at calculation time
      const revenueMinusDebt =
        ctx.decisionContext?.currentRevenueMinusDebt ?? cf.revenueMinusExistingDebt;
      const interestObligation =
        ctx.decisionContext?.currentInterestObligation ?? cf.interestObligation;
      const availableForBonds =
        ctx.decisionContext?.currentAvailableForBonds ?? cf.availableForBonds;
      return `Revenue After Debt ($${revenueMinusDebt?.toLocaleString()}) - Interest Obligation ($${interestObligation?.toLocaleString()}) = $${availableForBonds?.toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'revenue.totalForTaxYear',
    name: 'Total Revenue (Tax Year)',
    category: 'revenue',
    description: 'Total revenue for the related tax year: Taxable Value × Millage Rate / 1000',
    extractor: (ctx) => ctx.cashFlow?.totalRevenueForTaxYear,
    formatter: formatCurrency,
    formula: (ctx) => {
      const cf = ctx.cashFlow;
      if (!cf) return undefined;
      const taxYear = cf.relatedTaxBillYear;
      const taxableValue = ctx.revenueMap?.[taxYear]?.taxableValue;
      const millage = cf.millageRate;
      if (!taxableValue || !millage) return undefined;
      const calculated = (taxableValue * millage) / 1000;
      return `$${taxableValue.toLocaleString()} × ${millage.toFixed(4)} mills / 1000 = $${calculated.toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'revenue.paymentPercentage',
    name: 'Payment Percentage',
    category: 'revenue',
    description: 'Percentage of annual revenue for this payment',
    extractor: (ctx) => ctx.cashFlow?.paymentRevenuePercentage,
    formatter: formatPercent,
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'revenue.existingDebtPayment',
    name: 'Existing Debt Payment',
    category: 'revenue',
    description: 'Total existing debt payment this period',
    extractor: (ctx) => ctx.cashFlow?.debtPaymentAmount,
    formatter: formatCurrency,
    formula: (ctx) => {
      const cf = ctx.cashFlow;
      if (!cf) return undefined;
      return `Principal ($${cf.existingDebtPrincipal?.toLocaleString()}) + Interest ($${cf.existingDebtInterest?.toLocaleString()}) = $${cf.debtPaymentAmount?.toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'revenue.millageRate',
    name: 'Millage Rate',
    category: 'revenue',
    description: 'Millage rate for this tax year',
    extractor: (ctx) => ctx.cashFlow?.millageRate,
    formatter: formatMillage,
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'revenue.taxableValue',
    name: 'Taxable Value',
    category: 'revenue',
    description: 'Taxable value for this tax year - the base for revenue calculation',
    extractor: (ctx) => {
      const taxYear = ctx.cashFlow?.relatedTaxBillYear;
      if (!taxYear || !ctx.revenueMap) return undefined;
      return ctx.revenueMap[taxYear]?.taxableValue;
    },
    formatter: formatCurrency,
    formula: (ctx) => {
      const taxYear = ctx.cashFlow?.relatedTaxBillYear;
      if (!taxYear || !ctx.revenueMap) return undefined;
      const entry = ctx.revenueMap[taxYear];
      if (!entry) return `No revenue data for tax year ${taxYear}`;
      const growthRate = entry.growthRate;
      return `Tax Year ${taxYear}: $${entry.taxableValue.toLocaleString()} (${(growthRate * 100).toFixed(2)}% growth)`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },

  // === Revenue Carry-Forward from I/O Periods ===
  // When the NEXT payment (in processing order) is interest-only, its leftover
  // revenue is combined with the current period's revenue for principal distribution.
  // This is different from "interest accumulation" (future interest obligations).

  {
    id: 'revenue.nextPeriodAvailable',
    name: 'Next Period Available',
    category: 'revenue',
    description:
      'Revenue available from the next payment period. When next is I/O, this is carried forward to current.',
    extractor: (ctx) => {
      // Prefer decisionContext (accurate value at calculation time)
      if (ctx.decisionContext?.nextAvailableForBonds !== undefined) {
        return ctx.decisionContext.nextAvailableForBonds;
      }
      // Fallback to calculating from cashFlow
      const nextIndex = ctx.paymentIndex + 1;
      const cashFlows = ctx.candidate.cashFlows;
      if (!cashFlows || nextIndex >= cashFlows.length) return undefined;
      const next = cashFlows[nextIndex];
      return (next.revenueMinusExistingDebt ?? 0) - (next.interestObligation ?? 0);
    },
    formatter: formatCurrency,
    formula: (ctx) => {
      const nextIndex = ctx.paymentIndex + 1;
      const cashFlows = ctx.candidate.cashFlows;
      if (!cashFlows || nextIndex >= cashFlows.length) return 'No next payment';
      const next = cashFlows[nextIndex];
      // Use decisionContext values for accuracy
      const revMinusDebt =
        ctx.decisionContext?.nextRevenueMinusDebt ?? next.revenueMinusExistingDebt ?? 0;
      const interest = ctx.decisionContext?.nextInterestObligation ?? next.interestObligation ?? 0;
      const available = ctx.decisionContext?.nextAvailableForBonds ?? revMinusDebt - interest;
      if (next.isInterestOnly) {
        return `Next (${next.paymentDate}) is I/O: $${revMinusDebt.toLocaleString()} - $${interest.toLocaleString()} interest = $${available.toLocaleString()} carried forward`;
      }
      return `Next (${next.paymentDate}) is principal payment: $${available.toLocaleString()} (not carried forward)`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'revenue.combinedForDistribution',
    name: 'Combined Revenue for Distribution',
    category: 'revenue',
    description:
      'Total revenue available for principal distribution: current + next (if next is I/O).',
    extractor: (ctx) => {
      // Prefer decisionContext (accurate value at calculation time)
      if (ctx.decisionContext?.totalRevenueForDistribution !== undefined) {
        return ctx.decisionContext.totalRevenueForDistribution;
      }
      // Fallback to calculating from cashFlow
      const cf = ctx.cashFlow;
      if (!cf) return undefined;

      const currentAvailable = (cf.revenueMinusExistingDebt ?? 0) - (cf.interestObligation ?? 0);

      const nextIndex = ctx.paymentIndex + 1;
      const cashFlows = ctx.candidate.cashFlows;
      if (!cashFlows || nextIndex >= cashFlows.length) return currentAvailable;

      const next = cashFlows[nextIndex];
      if (!next.isInterestOnly) return currentAvailable;

      const nextAvailable = (next.revenueMinusExistingDebt ?? 0) - (next.interestObligation ?? 0);
      return currentAvailable + nextAvailable;
    },
    formatter: formatCurrency,
    formula: (ctx) => {
      const cf = ctx.cashFlow;
      if (!cf) return undefined;

      // Use decisionContext values for accuracy
      const currentAvailable =
        ctx.decisionContext?.currentAvailableForBonds ??
        (cf.revenueMinusExistingDebt ?? 0) - (cf.interestObligation ?? 0);

      const nextIndex = ctx.paymentIndex + 1;
      const cashFlows = ctx.candidate.cashFlows;
      if (!cashFlows || nextIndex >= cashFlows.length) {
        return `Current only: $${currentAvailable.toLocaleString()} (no next payment)`;
      }

      const next = cashFlows[nextIndex];
      const nextIsIO = ctx.decisionContext?.nextIsInterestOnly ?? next.isInterestOnly;
      if (!nextIsIO) {
        return `Current only: $${currentAvailable.toLocaleString()} (next is not I/O)`;
      }

      const nextAvailable =
        ctx.decisionContext?.nextAvailableForBonds ??
        (next.revenueMinusExistingDebt ?? 0) - (next.interestObligation ?? 0);
      const total =
        ctx.decisionContext?.totalRevenueForDistribution ?? currentAvailable + nextAvailable;
      return `Current ($${currentAvailable.toLocaleString()}) + Next I/O ($${nextAvailable.toLocaleString()}) = $${total.toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },

  // === Next Payment Context ===
  // These fields show information about the next payment in processing order
  // (which is the earlier payment in time, since processing goes backwards).
  // When the next payment is interest-only, its revenue is accumulated forward.

  {
    id: 'revenue.nextPaymentIsInterestOnly',
    name: 'Next Payment Is Interest Only',
    category: 'revenue',
    description:
      'Whether the next payment (in processing order) is interest-only. If true, that revenue is accumulated into this payment.',
    extractor: (ctx) => {
      const nextIndex = ctx.paymentIndex + 1;
      const cashFlows = ctx.candidate.cashFlows;
      if (!cashFlows || nextIndex >= cashFlows.length) return undefined;
      return cashFlows[nextIndex]?.isInterestOnly;
    },
    formatter: formatBoolean,
    formula: (ctx) => {
      const nextIndex = ctx.paymentIndex + 1;
      const cashFlows = ctx.candidate.cashFlows;
      if (!cashFlows || nextIndex >= cashFlows.length)
        return 'No next payment (first in processing order)';
      const next = cashFlows[nextIndex];
      return `Next payment (${next.paymentDate}): ${next.isInterestOnly ? 'Interest Only - revenue accumulates forward' : 'Principal + Interest'}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'revenue.nextPaymentRevenue',
    name: 'Next Payment Revenue',
    category: 'revenue',
    description:
      'Revenue portion from the next payment. When next payment is interest-only, this revenue is brought forward.',
    extractor: (ctx) => {
      const nextIndex = ctx.paymentIndex + 1;
      const cashFlows = ctx.candidate.cashFlows;
      if (!cashFlows || nextIndex >= cashFlows.length) return undefined;
      return cashFlows[nextIndex]?.paymentRevenuePortion;
    },
    formatter: formatCurrency,
    formula: (ctx) => {
      const nextIndex = ctx.paymentIndex + 1;
      const cashFlows = ctx.candidate.cashFlows;
      if (!cashFlows || nextIndex >= cashFlows.length) return 'No next payment';
      const next = cashFlows[nextIndex];
      const isIO = next.isInterestOnly;
      return `Next payment (${next.paymentDate}) revenue: $${next.paymentRevenuePortion?.toLocaleString() ?? 'N/A'}${isIO ? ' (accumulated - I/O period)' : ''}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'revenue.nextPaymentInterestObligation',
    name: 'Next Payment Interest Obligation',
    category: 'revenue',
    description:
      'Interest obligation from the next payment. When next payment is interest-only, this interest accumulates forward.',
    extractor: (ctx) => {
      // Prefer decisionContext (accurate value at calculation time)
      if (ctx.decisionContext?.nextInterestObligation !== undefined) {
        return ctx.decisionContext.nextInterestObligation;
      }
      // Fallback to cashFlow
      const nextIndex = ctx.paymentIndex + 1;
      const cashFlows = ctx.candidate.cashFlows;
      if (!cashFlows || nextIndex >= cashFlows.length) return undefined;
      return cashFlows[nextIndex]?.interestObligation;
    },
    formatter: formatCurrency,
    formula: (ctx) => {
      const nextIndex = ctx.paymentIndex + 1;
      const cashFlows = ctx.candidate.cashFlows;
      if (!cashFlows || nextIndex >= cashFlows.length) return 'No next payment';
      const next = cashFlows[nextIndex];
      const isIO = ctx.decisionContext?.nextIsInterestOnly ?? next.isInterestOnly;
      const interest = ctx.decisionContext?.nextInterestObligation ?? next.interestObligation;
      return `Next payment (${next.paymentDate}) interest: $${interest?.toLocaleString() ?? 'N/A'}${isIO ? ' (accumulated - I/O period)' : ''}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'revenue.nextPaymentDate',
    name: 'Next Payment Date',
    category: 'revenue',
    description: 'Date of the next payment in processing order (earlier in time).',
    extractor: (ctx) => {
      const nextIndex = ctx.paymentIndex + 1;
      const cashFlows = ctx.candidate.cashFlows;
      if (!cashFlows || nextIndex >= cashFlows.length) return undefined;
      return cashFlows[nextIndex]?.paymentDate;
    },
    formatter: (v) => (v !== undefined ? String(v) : 'N/A'),
    performanceCost: 'low',
    enabledByDefault: false,
  },
];
